import { useState, useEffect } from 'react';
import { X, Clock, Calendar, RefreshCw, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { useTheme } from './ThemeContext';

export default function ScheduleJobModal({ quote, onClose, onScheduled, recurringEnabled = false, companyId }) {
  const { colors } = useTheme();
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [estimatedDuration, setEstimatedDuration] = useState(60);
  const [scheduling, setScheduling] = useState(false);
  const [existingJobs, setExistingJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);

  // Week navigation
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
    const monday = new Date(today);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  });

  // Recurring job options
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceRule, setRecurrenceRule] = useState('weekly');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [conflicts, setConflicts] = useState([]);

  // Time slots for 24 hours
  const timeSlots = [];
  for (let hour = 0; hour <= 23; hour++) {
    timeSlots.push(`${hour}:00`);
  }

  // Get week dates
  const getWeekDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const weekDates = getWeekDates();

  // Format date for display
  const formatDateShort = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatDateISO = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Load existing jobs for the week
  useEffect(() => {
    const loadJobs = async () => {
      if (!companyId && !quote?.company_id) return;

      setLoadingJobs(true);
      const startDate = formatDateISO(weekDates[0]);
      const endDate = formatDateISO(weekDates[6]);

      const { data, error } = await supabaseData
        .from('jobs')
        .select('*, customers(name)')
        .eq('company_id', companyId || quote?.company_id)
        .gte('scheduled_date', startDate)
        .lte('scheduled_date', endDate)
        .not('status', 'eq', 'cancelled');

      if (!error && data) {
        setExistingJobs(data);
      }
      setLoadingJobs(false);
    };

    loadJobs();
  }, [weekStart, companyId, quote?.company_id]);

  // Check for conflicts when recurring is enabled
  useEffect(() => {
    if (!isRecurring || !selectedDate || !recurrenceEndDate) {
      setConflicts([]);
      return;
    }

    const checkConflicts = async () => {
      // Generate all occurrence dates
      const occurrences = generateOccurrences(selectedDate, recurrenceEndDate, recurrenceRule);

      // Fetch all jobs in that date range
      const { data: futureJobs } = await supabaseData
        .from('jobs')
        .select('scheduled_date, scheduled_time, customers(name)')
        .eq('company_id', companyId || quote?.company_id)
        .gte('scheduled_date', selectedDate)
        .lte('scheduled_date', recurrenceEndDate)
        .not('status', 'eq', 'cancelled');

      if (!futureJobs) return;

      // Find conflicts
      const foundConflicts = [];
      occurrences.forEach(occDate => {
        futureJobs.forEach(job => {
          if (job.scheduled_date === occDate && job.scheduled_time === selectedTime) {
            foundConflicts.push({
              date: occDate,
              time: job.scheduled_time,
              customerName: job.customers?.name || 'Unknown'
            });
          }
        });
      });

      setConflicts(foundConflicts);
    };

    checkConflicts();
  }, [isRecurring, selectedDate, selectedTime, recurrenceEndDate, recurrenceRule]);

  // Generate occurrence dates for recurring jobs
  const generateOccurrences = (startDate, endDate, rule) => {
    const occurrences = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    let current = new Date(start);

    while (current <= end) {
      occurrences.push(formatDateISO(current));

      switch (rule) {
        case 'weekly':
          current.setDate(current.getDate() + 7);
          break;
        case 'biweekly':
          current.setDate(current.getDate() + 14);
          break;
        case 'monthly':
          current.setMonth(current.getMonth() + 1);
          break;
        default:
          current.setDate(current.getDate() + 7);
      }
    }

    return occurrences;
  };

  // Get jobs for a specific date and time
  const getJobsAt = (date, time) => {
    const dateStr = formatDateISO(date);
    return existingJobs.filter(job => {
      if (job.scheduled_date !== dateStr) return false;
      if (!job.scheduled_time) return false;

      const jobHour = parseInt(job.scheduled_time.split(':')[0]);
      const slotHour = parseInt(time.split(':')[0]);
      const duration = job.estimated_duration || 60;
      const jobEndHour = jobHour + Math.ceil(duration / 60);

      return slotHour >= jobHour && slotHour < jobEndHour;
    });
  };

  // Check if a slot is the start of a job
  const isJobStart = (date, time) => {
    const dateStr = formatDateISO(date);
    return existingJobs.find(job =>
      job.scheduled_date === dateStr &&
      job.scheduled_time?.startsWith(time.split(':')[0] + ':')
    );
  };

  // Check if date is in the past
  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  // Navigate weeks
  const prevWeek = () => {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() - 7);
    setWeekStart(newStart);
  };

  const nextWeek = () => {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() + 7);
    setWeekStart(newStart);
  };

  const goToToday = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(today);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0);
    setWeekStart(monday);
  };

  // Handle slot click
  const handleSlotClick = (date, time) => {
    if (isPastDate(date)) return;

    const dateStr = formatDateISO(date);
    const jobsAtSlot = getJobsAt(date, time);

    if (jobsAtSlot.length > 0) {
      // Slot is busy - could show a warning or just prevent selection
      return;
    }

    setSelectedDate(dateStr);
    setSelectedTime(time);
  };

  // Handle schedule
  const handleSchedule = async () => {
    if (!selectedDate || !selectedTime) {
      alert('Please select a date and time');
      return;
    }

    if (isRecurring && !recurrenceEndDate) {
      alert('Please select an end date for recurring jobs');
      return;
    }

    if (conflicts.length > 0) {
      const proceed = window.confirm(
        `Warning: ${conflicts.length} conflict(s) detected with existing jobs. Continue anyway?`
      );
      if (!proceed) return;
    }

    setScheduling(true);
    try {
      const recurringOptions = isRecurring ? {
        is_recurring: true,
        recurrence_rule: recurrenceRule,
        recurrence_end_date: recurrenceEndDate
      } : {
        is_recurring: false
      };

      await onScheduled(selectedDate, selectedTime, estimatedDuration, recurringOptions);
      onClose();
    } catch (error) {
      alert('Failed to schedule job');
    } finally {
      setScheduling(false);
    }
  };

  // Format time for display
  const formatTimeDisplay = (time) => {
    const hour = parseInt(time.split(':')[0]);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12} ${ampm}`;
  };

  // Check if this is the selected slot
  const isSelected = (date, time) => {
    return formatDateISO(date) === selectedDate && time === selectedTime;
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return formatDateISO(date) === formatDateISO(today);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '1rem'
    }}>
      <div style={{
        background: colors.bgCard,
        borderRadius: '12px',
        width: '100%',
        maxWidth: '1000px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${colors.border}`
      }}>
        {/* Header */}
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <div>
            <h2 style={{ margin: 0, color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Calendar size={24} />
              Schedule Job
            </h2>
            {quote && (
              <div style={{ color: colors.textMuted, fontSize: '0.9rem', marginTop: '0.25rem' }}>
                {quote.customer?.name || 'Customer'} • ${quote.total?.toFixed(2)}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: colors.textMuted,
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Week Navigation */}
        <div style={{
          padding: '0.75rem 1.5rem',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: colors.bgInput,
          flexShrink: 0
        }}>
          <button
            onClick={prevWeek}
            style={{
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              padding: '0.5rem 0.75rem',
              color: colors.text,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}
          >
            <ChevronLeft size={18} />
            Prev
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span style={{ fontWeight: 'bold', color: colors.text }}>
              {weekDates[0].toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <button
              onClick={goToToday}
              style={{
                background: colors.accent,
                border: 'none',
                borderRadius: '6px',
                padding: '0.4rem 0.75rem',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 'bold'
              }}
            >
              Today
            </button>
          </div>

          <button
            onClick={nextWeek}
            style={{
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              padding: '0.5rem 0.75rem',
              color: colors.text,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}
          >
            Next
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Calendar Grid */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '1rem'
        }}>
          {loadingJobs ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: colors.textMuted }}>
              Loading schedule...
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '60px repeat(7, 1fr)',
              gap: '1px',
              background: colors.border,
              borderRadius: '8px',
              overflow: 'hidden',
              minWidth: '700px'
            }}>
              {/* Header Row */}
              <div style={{ background: colors.bgInput, padding: '0.5rem' }}></div>
              {weekDates.map((date, idx) => (
                <div
                  key={idx}
                  style={{
                    background: isToday(date) ? colors.accent : colors.bgInput,
                    padding: '0.75rem 0.5rem',
                    textAlign: 'center',
                    color: isToday(date) ? '#fff' : colors.text,
                    fontWeight: isToday(date) ? 'bold' : 'normal'
                  }}
                >
                  <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                    {date.toLocaleDateString('en-US', { weekday: 'short' })}
                  </div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                    {date.getDate()}
                  </div>
                </div>
              ))}

              {/* Time Rows */}
              {timeSlots.map((time) => (
                <>
                  {/* Time Label */}
                  <div
                    key={`label-${time}`}
                    style={{
                      background: colors.bgCard,
                      padding: '0.5rem',
                      fontSize: '0.75rem',
                      color: colors.textMuted,
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'center'
                    }}
                  >
                    {formatTimeDisplay(time)}
                  </div>

                  {/* Day Slots */}
                  {weekDates.map((date, dayIdx) => {
                    const jobsAtSlot = getJobsAt(date, time);
                    const jobStart = isJobStart(date, time);
                    const isPast = isPastDate(date);
                    const selected = isSelected(date, time);
                    const hasJob = jobsAtSlot.length > 0;

                    return (
                      <div
                        key={`${time}-${dayIdx}`}
                        onClick={() => handleSlotClick(date, time)}
                        style={{
                          background: selected
                            ? colors.accent
                            : hasJob
                            ? `${colors.info}40`
                            : isPast
                            ? colors.bgInput
                            : colors.bgCard,
                          padding: '0.25rem',
                          minHeight: '45px',
                          cursor: isPast || hasJob ? 'not-allowed' : 'pointer',
                          position: 'relative',
                          opacity: isPast ? 0.5 : 1,
                          transition: 'background 0.15s',
                          border: selected ? `2px solid ${colors.accent}` : 'none'
                        }}
                        onMouseEnter={(e) => {
                          if (!isPast && !hasJob && !selected) {
                            e.currentTarget.style.background = `${colors.accent}30`;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isPast && !hasJob && !selected) {
                            e.currentTarget.style.background = colors.bgCard;
                          }
                        }}
                      >
                        {jobStart && (
                          <div style={{
                            background: colors.info,
                            color: '#fff',
                            padding: '0.2rem 0.4rem',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {jobStart.customers?.name || 'Job'}
                          </div>
                        )}
                        {selected && (
                          <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: '#fff',
                            fontWeight: 'bold',
                            fontSize: '0.75rem'
                          }}>
                            Selected
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              ))}
            </div>
          )}
        </div>

        {/* Bottom Panel - Selection & Options */}
        <div style={{
          borderTop: `1px solid ${colors.border}`,
          padding: '1rem 1.5rem',
          background: colors.bgInput,
          flexShrink: 0
        }}>
          {/* Selection Summary */}
          {selectedDate && selectedTime ? (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '1rem',
              alignItems: 'center',
              marginBottom: '1rem'
            }}>
              <div style={{
                background: colors.accent,
                color: '#fff',
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                fontWeight: 'bold'
              }}>
                {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                })} at {selectedTime}
              </div>

              {/* Duration */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ color: colors.textMuted, fontSize: '0.9rem' }}>Duration:</span>
                <select
                  value={estimatedDuration}
                  onChange={(e) => setEstimatedDuration(parseInt(e.target.value))}
                  style={{
                    padding: '0.4rem 0.75rem',
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text,
                    fontSize: '0.9rem'
                  }}
                >
                  <option value={30}>30 min</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                  <option value={180}>3 hours</option>
                  <option value={240}>4 hours</option>
                </select>
              </div>

              {/* Recurring Toggle */}
              {recurringEnabled && (
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer',
                  padding: '0.4rem 0.75rem',
                  background: isRecurring ? '#9b59b630' : colors.bgCard,
                  border: `1px solid ${isRecurring ? '#9b59b6' : colors.border}`,
                  borderRadius: '6px'
                }}>
                  <RefreshCw size={16} color={isRecurring ? '#9b59b6' : colors.textMuted} />
                  <span style={{ color: colors.text, fontSize: '0.9rem' }}>Recurring</span>
                  <input
                    type="checkbox"
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                    style={{ marginLeft: '0.25rem' }}
                  />
                </label>
              )}
            </div>
          ) : (
            <div style={{
              color: colors.textMuted,
              marginBottom: '1rem',
              padding: '0.75rem',
              background: colors.bgCard,
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              Click a time slot on the calendar to select
            </div>
          )}

          {/* Recurring Options */}
          {isRecurring && selectedDate && (
            <div style={{
              background: '#9b59b620',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1rem',
              border: '1px solid #9b59b6'
            }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ color: colors.text }}>Repeat:</span>
                  <select
                    value={recurrenceRule}
                    onChange={(e) => setRecurrenceRule(e.target.value)}
                    style={{
                      padding: '0.4rem 0.75rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text
                    }}
                  >
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Every 2 Weeks</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ color: colors.text }}>Until:</span>
                  <input
                    type="date"
                    value={recurrenceEndDate}
                    onChange={(e) => setRecurrenceEndDate(e.target.value)}
                    min={selectedDate}
                    style={{
                      padding: '0.4rem 0.75rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text
                    }}
                  />
                </div>

                {recurrenceEndDate && (
                  <div style={{ color: '#9b59b6', fontSize: '0.85rem' }}>
                    {generateOccurrences(selectedDate, recurrenceEndDate, recurrenceRule).length} occurrences
                  </div>
                )}
              </div>

              {/* Conflict Warnings */}
              {conflicts.length > 0 && (
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem',
                  background: `${colors.error}20`,
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem'
                }}>
                  <AlertTriangle size={18} color={colors.error} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <div style={{ color: colors.error, fontWeight: 'bold', marginBottom: '0.25rem' }}>
                      {conflicts.length} Conflict(s) Detected
                    </div>
                    <div style={{ color: colors.text, fontSize: '0.85rem' }}>
                      {conflicts.slice(0, 3).map((c, i) => (
                        <div key={i}>
                          {new Date(c.date + 'T12:00:00').toLocaleDateString()} - {c.customerName}
                        </div>
                      ))}
                      {conflicts.length > 3 && (
                        <div style={{ color: colors.textMuted }}>
                          ...and {conflicts.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              onClick={handleSchedule}
              disabled={!selectedDate || !selectedTime || scheduling}
              style={{
                flex: 1,
                background: selectedDate && selectedTime && !scheduling ? colors.success : colors.bgCard,
                color: selectedDate && selectedTime && !scheduling ? '#fff' : colors.textMuted,
                border: 'none',
                padding: '1rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: selectedDate && selectedTime && !scheduling ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {scheduling ? 'Scheduling...' : (
                <>
                  <Calendar size={20} />
                  Schedule Job
                </>
              )}
            </button>

            <button
              onClick={onClose}
              disabled={scheduling}
              style={{
                padding: '1rem 2rem',
                background: colors.bgCard,
                color: colors.text,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                fontSize: '1rem',
                cursor: scheduling ? 'not-allowed' : 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
